import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import type { CSSProperties } from 'react'
import CloseRoundedIcon from '@mui/icons-material/CloseRounded'
import { Button } from '../Common/Button'
import { Pressable } from '../Common/Pressable'
import { useTimeline } from '../../context/useTimeline'
import { useNavigation } from '../../context/useNavigation'
import type { TimelineElement } from '../../context/TimelineContext'
import type { DashboardViewProps } from '../../types/dashboardView'
import type { WorkspaceActivityRecord } from '../../services/activityService'

/**
 * Grid View — horizontal week-column calendar of timeline entities.
 *
 * (File name is the legacy "ContributionHeatmapView" from the earlier
 * contribution-count concept. The view is now entirely a Grid showing
 * goals/tasks/subtasks colored by their date span, gated by navigation level.)
 *
 * Layout:
 *   - Columns = weeks (Mon-start). Each column has a date label above it
 *     showing the Monday's date; labels scroll horizontally with the grid.
 *   - Rows = 7 weekdays, top to bottom Mon … Sun. No left-side day labels.
 *   - Cells = single days; gray when nothing is scheduled, colored by the
 *     spanning entities otherwise. Multiple overlapping entities → horizontal
 *     bands inside the cell.
 *
 * Drill behavior (driven by `useNavigation()`):
 *   - dashboard → render GOALs as cell colors.
 *   - goal      → frame the active goal's span with a border; render the
 *                 goal's TASKs as cell colors inside that frame.
 *   - task /    → frame the active task's span; render the task's SUBTASKs.
 *     subtask
 *
 * Scroll window:
 *   - Default visible: 12 weeks centered on today's week (6 back / 5 forward).
 *   - Scroll bounds extend to ±12 weeks past the earliest/latest entity date
 *     in the current drill view.
 *   - Edges fade + zoom out per IntersectionObserver, matching the spec's
 *     reference HTML.
 */

const DAYS_PER_WEEK = 7
const DEFAULT_WEEKS_BEFORE_TODAY = 6
const DEFAULT_WEEKS_AFTER_TODAY = 5
const BOUNDS_PADDING_WEEKS = 12
const CELL_PX = 36
const CELL_GAP_PX = 6
const COL_GAP_PX = 14
const DEFAULT_GRAY = 'var(--surface-muted)'
const IN_SCOPE_EMPTY_GRAY = 'var(--control)'
const DAY_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']
const MONTH_BAND_HEIGHT = 22
const WEEK_LABEL_HEIGHT = 22
const DAY_RAIL_WIDTH = 44
const TRACK_INNER_PAD = 32

const startOfDay = (date: Date): Date => {
  const next = new Date(date)
  next.setHours(0, 0, 0, 0)
  return next
}

/** Mon-aligned start of the week containing `date`. */
const startOfWeekMonday = (date: Date): Date => {
  const next = startOfDay(date)
  const dayOfWeek = next.getDay() // 0=Sun, 1=Mon, …, 6=Sat
  // Distance to most recent Monday: Sun -> 6, Mon -> 0, Tue -> 1, …
  const distance = (dayOfWeek + 6) % 7
  next.setDate(next.getDate() - distance)
  return next
}

const addDays = (date: Date, days: number): Date => {
  const next = new Date(date)
  next.setDate(next.getDate() + days)
  return next
}

const dateKey = (date: Date): string => {
  const y = date.getFullYear()
  const m = String(date.getMonth() + 1).padStart(2, '0')
  const d = String(date.getDate()).padStart(2, '0')
  return `${y}-${m}-${d}`
}

/** Parse a YYYY-MM-DD (or ISO datetime) string into a local-day Date. */
const parseDate = (raw: string | null | undefined): Date | null => {
  if (!raw) return null
  const head = raw.length >= 10 ? raw.slice(0, 10) : raw
  if (!/^\d{4}-\d{2}-\d{2}$/.test(head)) return null
  const [y, m, d] = head.split('-').map(Number)
  return new Date(y, m - 1, d)
}

const SHORT_MONTH_DAY: Intl.DateTimeFormatOptions = { month: 'short', day: 'numeric' }

/** What level of the tree do we show colored, given current navigation? */
type DrillContext = {
  /** The entity type that renders as colored cells. */
  cellType: TimelineElement['type']
  /** The "framed" parent whose span renders as bordered cells, or null at dashboard. */
  frameParent: TimelineElement | null
  /** True when there is a parent frame to render. */
  hasFrame: boolean
}

const resolveDrillContext = (
  level: string,
  activeGoalId: string | null,
  activeTaskId: string | null,
  elementsById: Map<string, TimelineElement>,
): DrillContext => {
  if (level === 'dashboard') {
    return { cellType: 'GOAL', frameParent: null, hasFrame: false }
  }
  if (level === 'goal' && activeGoalId) {
    const parent = elementsById.get(activeGoalId) ?? null
    return { cellType: 'TASK', frameParent: parent, hasFrame: parent !== null }
  }
  // 'task' and 'subtask' both render the task's subtasks; subtask drill keeps
  // the same view since subtasks are leaves (per the user spec — no deeper drill).
  if ((level === 'task' || level === 'subtask') && activeTaskId) {
    const parent = elementsById.get(activeTaskId) ?? null
    return { cellType: 'SUBTASK', frameParent: parent, hasFrame: parent !== null }
  }
  return { cellType: 'GOAL', frameParent: null, hasFrame: false }
}

/** Filter children of `parent` (one level down) that have valid date spans. */
const filterCellEntities = (
  cellType: TimelineElement['type'],
  parent: TimelineElement | null,
  elements: TimelineElement[],
): TimelineElement[] => {
  if (cellType === 'GOAL') {
    return elements.filter((el) => el.type === 'GOAL')
  }
  if (!parent) return []
  return elements.filter((el) => el.type === cellType && el.parentId === parent.id)
}

/** A solid color for an entity, falling back to accent. */
const colorOf = (entity: TimelineElement): string => entity.color ?? 'var(--accent)'

const buildSpans = (
  entities: TimelineElement[],
): Array<{ entity: TimelineElement; startMs: number; endMs: number; color: string }> => {
  const spans: Array<{ entity: TimelineElement; startMs: number; endMs: number; color: string }> = []
  for (const el of entities) {
    const start = parseDate(el.startDate)
    const end = parseDate(el.endDate) ?? start
    if (!start || !end) continue
    spans.push({
      entity: el,
      startMs: startOfDay(start).getTime(),
      endMs: startOfDay(end).getTime(),
      color: colorOf(el),
    })
  }
  return spans
}

/** Returns every entity + color whose span covers the given date. */
const entitiesForDay = (
  date: Date,
  entitySpans: Array<{ entity: TimelineElement; startMs: number; endMs: number; color: string }>,
): Array<{ entity: TimelineElement; color: string }> => {
  const dayMs = startOfDay(date).getTime()
  const hits: Array<{ entity: TimelineElement; color: string }> = []
  for (const span of entitySpans) {
    if (dayMs >= span.startMs && dayMs <= span.endMs) hits.push({ entity: span.entity, color: span.color })
  }
  return hits
}

/**
 * CSS background producing N horizontal color bands. Single color → flat fill;
 * N colors → hard-edged linear-gradient with N equal stripes.
 */
const stripeBackground = (colors: string[]): string | undefined => {
  if (colors.length === 0) return undefined
  if (colors.length === 1) return colors[0]
  const segmentPct = 100 / colors.length
  const stops = colors
    .map((color, index) => {
      const from = (index * segmentPct).toFixed(2)
      const to = ((index + 1) * segmentPct).toFixed(2)
      return `${color} ${from}% ${to}%`
    })
    .join(', ')
  return `linear-gradient(to bottom, ${stops})`
}

interface WeekColumn {
  /** Monday of this week. */
  weekStart: Date
  /** The 7 dates in this week, Mon → Sun. */
  days: Date[]
  /** Stable identifier for React keys. */
  key: string
}

/** All week-columns from `boundsStart` (Mon) through `boundsEnd` inclusive. */
const buildColumns = (boundsStart: Date, boundsEnd: Date): WeekColumn[] => {
  const columns: WeekColumn[] = []
  const cursor = new Date(boundsStart)
  // Hard cap to keep accidental garbage data from blowing up DOM.
  for (let i = 0; i < 520 && cursor.getTime() <= boundsEnd.getTime(); i += 1) {
    const days: Date[] = []
    for (let d = 0; d < DAYS_PER_WEEK; d += 1) days.push(addDays(cursor, d))
    columns.push({ weekStart: new Date(cursor), days, key: dateKey(cursor) })
    cursor.setDate(cursor.getDate() + DAYS_PER_WEEK)
  }
  return columns
}

export const GridView = () => {
  const { elements } = useTimeline()
  const { currentLevel, activeGoalId, activeTaskId, goToGoal, goToTask, goToSubTask } = useNavigation()

  const elementsById = useMemo(() => {
    const map = new Map<string, TimelineElement>()
    for (const el of elements) map.set(el.id, el)
    return map
  }, [elements])

  const drill = useMemo(
    () => resolveDrillContext(currentLevel, activeGoalId, activeTaskId, elementsById),
    [currentLevel, activeGoalId, activeTaskId, elementsById],
  )

  const cellEntities = useMemo(
    () => filterCellEntities(drill.cellType, drill.frameParent, elements),
    [drill.cellType, drill.frameParent, elements],
  )

  const cellSpans = useMemo(() => buildSpans(cellEntities), [cellEntities])

  const frameSpan = useMemo<{ startMs: number; endMs: number; color: string } | null>(() => {
    if (!drill.frameParent) return null
    const start = parseDate(drill.frameParent.startDate)
    const end = parseDate(drill.frameParent.endDate) ?? start
    if (!start || !end) return null
    return {
      startMs: startOfDay(start).getTime(),
      endMs: startOfDay(end).getTime(),
      color: colorOf(drill.frameParent),
    }
  }, [drill.frameParent])

  const todayMs = useMemo(() => startOfDay(new Date()).getTime(), [])
  const todayWeekStart = useMemo(() => startOfWeekMonday(new Date()), [])

  const bounds = useMemo(() => {
    const defaultLeft = addDays(todayWeekStart, -DEFAULT_WEEKS_BEFORE_TODAY * DAYS_PER_WEEK)
    const defaultRight = addDays(todayWeekStart, (DEFAULT_WEEKS_AFTER_TODAY + 1) * DAYS_PER_WEEK - 1)

    let leftMs = defaultLeft.getTime()
    let rightMs = defaultRight.getTime()

    const consider = (raw: string | null | undefined) => {
      const parsed = parseDate(raw)
      if (!parsed) return
      leftMs = Math.min(leftMs, parsed.getTime())
      rightMs = Math.max(rightMs, parsed.getTime())
    }
    for (const el of elements) {
      consider(el.startDate)
      consider(el.endDate)
    }

    const paddedLeft = startOfWeekMonday(addDays(new Date(leftMs), -BOUNDS_PADDING_WEEKS * DAYS_PER_WEEK))
    const paddedRight = startOfWeekMonday(addDays(new Date(rightMs), BOUNDS_PADDING_WEEKS * DAYS_PER_WEEK))
    return { start: paddedLeft, end: paddedRight }
  }, [elements, todayWeekStart])

  const columns = useMemo(() => buildColumns(bounds.start, bounds.end), [bounds])

  const todayWeekColIndex = useMemo(() => {
    const weekMs = todayWeekStart.getTime()
    return columns.findIndex((col) => col.weekStart.getTime() === weekMs)
  }, [columns, todayWeekStart])

  // ── Responsive scale via ResizeObserver on the scroll track. ──
  const trackRef = useRef<HTMLDivElement>(null)
  const [trackWidth, setTrackWidth] = useState(1200)
  useLayoutEffect(() => {
    const node = trackRef.current
    if (!node) return
    const initialW = node.clientWidth
    if (initialW) setTrackWidth(initialW)
    const observer = new ResizeObserver((entries) => {
      const w = entries[0]?.contentRect.width
      if (w) setTrackWidth(w)
    })
    observer.observe(node)
    return () => observer.disconnect()
  }, [])

  const scale = Math.min(1.35, Math.max(0.85, trackWidth / 1200))
  const cellSize = CELL_PX * scale
  const cellGap = CELL_GAP_PX * scale
  const colGap = COL_GAP_PX * scale
  const colStride = cellSize + colGap

  // ── Month bands aggregated from week columns. ──
  const monthBands = useMemo(() => {
    const bands: { key: string; label: string; startColIdx: number; endColIdx: number }[] = []
    columns.forEach((col, idx) => {
      const d = col.weekStart
      const key = `${d.getFullYear()}-${d.getMonth()}`
      const last = bands[bands.length - 1]
      if (last && last.key === key) {
        last.endColIdx = idx
      } else {
        bands.push({
          key,
          label: d.toLocaleDateString(undefined, { month: 'short', year: 'numeric' }).toUpperCase(),
          startColIdx: idx,
          endColIdx: idx,
        })
      }
    })
    return bands
  }, [columns])

  // ── Landing scroll: preserves original semantics (today-active → today,
  //     else upcoming → earliest, else today). Now uses scaled colStride. ──
  const lastDrillKeyRef = useRef<string | null>(null)
  const drillKey = `${currentLevel}|${activeGoalId ?? ''}|${activeTaskId ?? ''}`

  useLayoutEffect(() => {
    if (lastDrillKeyRef.current === drillKey || !trackRef.current || columns.length === 0) return
    const track = trackRef.current

    const findColIndexForDate = (targetMs: number): number => {
      const targetWeekStart = startOfWeekMonday(new Date(targetMs)).getTime()
      return columns.findIndex((col) => col.weekStart.getTime() === targetWeekStart)
    }

    const scrollCenterOnColLocal = (colIndex: number) => {
      const targetCenter = TRACK_INNER_PAD + colIndex * colStride + cellSize / 2
      track.scrollLeft = Math.max(0, targetCenter - track.clientWidth / 2)
    }

    const scrollLeftAlignedOnColLocal = (colIndex: number) => {
      const targetLeft = TRACK_INNER_PAD + colIndex * colStride
      track.scrollLeft = Math.max(0, targetLeft - track.clientWidth * 0.25)
    }

    const activeSpan = cellSpans.find((s) => todayMs >= s.startMs && todayMs <= s.endMs)
    if (activeSpan) {
      const idx = findColIndexForDate(todayMs)
      if (idx >= 0) {
        scrollCenterOnColLocal(idx)
        lastDrillKeyRef.current = drillKey
        return
      }
    }

    const upcoming = cellSpans.filter((s) => s.startMs > todayMs)
    if (upcoming.length > 0) {
      const earliest = upcoming.reduce((a, b) => (a.startMs <= b.startMs ? a : b))
      const idx = findColIndexForDate(earliest.startMs)
      if (idx >= 0) {
        scrollLeftAlignedOnColLocal(idx)
        lastDrillKeyRef.current = drillKey
        return
      }
    }

    const idx = findColIndexForDate(todayMs)
    if (idx >= 0) {
      scrollCenterOnColLocal(idx)
      lastDrillKeyRef.current = drillKey
    }
  }, [columns, todayWeekStart, cellSpans, todayMs, drillKey, colStride, cellSize])

  const scrollToToday = () => {
    const track = trackRef.current
    if (!track || todayWeekColIndex < 0) return
    const targetCenter = TRACK_INNER_PAD + todayWeekColIndex * colStride + cellSize / 2
    track.scrollTo({ left: Math.max(0, targetCenter - track.clientWidth / 2), behavior: 'smooth' })
  }

  // ── Edge zoom + fade via IntersectionObserver on each column. ──
  const columnRefs = useRef<Map<string, HTMLDivElement | null>>(new Map())
  useEffect(() => {
    const track = trackRef.current
    if (!track) return
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          const el = entry.target as HTMLElement
          const ratio = entry.intersectionRatio
          const factor = Math.min(1, ratio * 1.5)
          el.style.transform = `scale(${0.4 + factor * 0.6})`
          el.style.opacity = String(factor)
        }
      },
      {
        root: track,
        threshold: Array.from({ length: 21 }, (_, i) => i / 20),
      },
    )
    columnRefs.current.forEach((node) => {
      if (node) observer.observe(node)
    })
    return () => observer.disconnect()
  }, [columns])

  // ── Today-visibility observer (drives back-to-today pill). ──
  const [isTodayInView, setIsTodayInView] = useState(true)
  const todayColumnRef = useRef<HTMLDivElement | null>(null)
  useEffect(() => {
    const track = trackRef.current
    const targetNode = todayColumnRef.current
    if (!track || !targetNode) {
      setIsTodayInView(false)
      return
    }
    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0]
        setIsTodayInView(entry.intersectionRatio > 0.3)
      },
      { root: track, threshold: [0, 0.3, 0.6] },
    )
    observer.observe(targetNode)
    return () => observer.disconnect()
  }, [todayWeekColIndex, columns])

  // ── Drag-to-scroll. ──
  const dragStateRef = useRef<{ active: boolean; startX: number; startScroll: number }>({
    active: false,
    startX: 0,
    startScroll: 0,
  })
  const handleMouseDown = (event: React.MouseEvent<HTMLDivElement>) => {
    const track = trackRef.current
    if (!track) return
    dragStateRef.current = {
      active: true,
      startX: event.pageX - track.offsetLeft,
      startScroll: track.scrollLeft,
    }
  }
  const handleMouseMove = (event: React.MouseEvent<HTMLDivElement>) => {
    const state = dragStateRef.current
    if (!state.active) return
    const track = trackRef.current
    if (!track) return
    event.preventDefault()
    const x = event.pageX - track.offsetLeft
    track.scrollLeft = state.startScroll - (x - state.startX) * 1.4
  }
  const endDrag = () => {
    dragStateRef.current.active = false
  }

  const spineHeight = WEEK_LABEL_HEIGHT + 6 + (cellSize + cellGap) * DAYS_PER_WEEK

  return (
    <div className="flex flex-col items-stretch w-full h-full text-[var(--text)] select-none">
      {/* ── Drill breadcrumb + back-to-today pill ─────────────────── */}
      <div className="px-6 pt-5 pb-2 flex items-center gap-3">
        <div className="flex items-center gap-2 text-xs text-[var(--muted)] min-w-0">
          <span className="uppercase tracking-[0.18em] text-[10px] text-[var(--muted)]">Grid view</span>
          {drill.frameParent ? (
            <>
              <span>·</span>
              <span className="text-[var(--text-strong)] font-medium truncate">
                {drill.frameParent.title}
              </span>
              <span>·</span>
              <span className="truncate">
                showing {drill.cellType === 'TASK' ? 'tasks' : 'subtasks'} · {cellEntities.length} item{cellEntities.length === 1 ? '' : 's'}
              </span>
            </>
          ) : (
            <>
              <span>·</span>
              <span>showing goals · {cellEntities.length} item{cellEntities.length === 1 ? '' : 's'}</span>
            </>
          )}
        </div>
        {todayWeekColIndex >= 0 && !isTodayInView && (
          <Button
            variant="secondary"
            size="sm"
            onClick={scrollToToday}
            className="ml-auto shrink-0"
            aria-label="Scroll back to today"
            title="Scroll to today"
          >
            ↩ Today
          </Button>
        )}
      </div>

      {/* ── Rail + scrollable weeks ────────────────────────────────── */}
      <div className="flex flex-1 min-h-0 gap-2 px-6 pb-6">
        {/* Day-of-week rail (fixed left) */}
        <div
          className="flex flex-col shrink-0 pt-4"
          style={{ width: DAY_RAIL_WIDTH }}
          aria-hidden
        >
          <div style={{ height: MONTH_BAND_HEIGHT + WEEK_LABEL_HEIGHT + 6 }} />
          <div className="flex flex-col" style={{ gap: cellGap }}>
            {DAY_LABELS.map((label, idx) => (
              <div
                key={label}
                className={`grid place-items-center text-[10px] font-black uppercase tracking-wider ${
                  idx >= 5 ? 'text-[var(--muted)]/60' : 'text-[var(--muted)]'
                }`}
                style={{ height: cellSize }}
              >
                {label}
              </div>
            ))}
          </div>
        </div>

        {/* Scrollable track */}
        <div
          className="flex-1 relative overflow-hidden rounded-lg border border-[var(--border)] bg-[var(--surface-muted)]"
          style={{
            maskImage: 'linear-gradient(to right, transparent, black 4%, black 96%, transparent)',
            WebkitMaskImage: 'linear-gradient(to right, transparent, black 4%, black 96%, transparent)',
          }}
        >
          <div
            ref={trackRef}
            data-dashboard-horizontal-scroll="true"
            onMouseDown={handleMouseDown}
            onMouseMove={handleMouseMove}
            onMouseUp={endDrag}
            onMouseLeave={endDrag}
            className="absolute inset-0 overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden cursor-grab active:cursor-grabbing"
          >
            <div className="relative h-full flex flex-col pt-4" style={{ paddingLeft: TRACK_INNER_PAD, paddingRight: TRACK_INNER_PAD }}>
              {/* Month band */}
              <div className="relative shrink-0" style={{ height: MONTH_BAND_HEIGHT }}>
                {monthBands.map((band) => {
                  const left = band.startColIdx * colStride
                  const width = (band.endColIdx - band.startColIdx + 1) * colStride - colGap
                  return (
                    <div
                      key={band.key}
                      className="absolute top-0 flex items-center gap-2 text-[10px] font-black uppercase tracking-[0.18em]"
                      style={{ left, width, height: MONTH_BAND_HEIGHT }}
                    >
                      <span className="whitespace-nowrap text-[var(--text-strong)]/80">{band.label}</span>
                      <span className="flex-1 border-t border-[var(--border-subtle)]" />
                    </div>
                  )
                })}
              </div>

              {/* Week label row + cell rows */}
              <div className="flex" style={{ gap: colGap }}>
                {columns.map((column, colIdx) => {
                  const isTodayWeek = colIdx === todayWeekColIndex
                  return (
                    <div
                      key={column.key}
                      ref={(node) => {
                        columnRefs.current.set(column.key, node)
                        if (colIdx === todayWeekColIndex) todayColumnRef.current = node
                      }}
                      className="flex flex-col items-stretch shrink-0 origin-center transition-[transform,opacity] duration-150 ease-out relative"
                      style={{ width: cellSize, gap: cellGap }}
                    >
                      {/* Today spine */}
                      {isTodayWeek && (
                        <div
                          aria-hidden
                          className="pointer-events-none absolute left-1/2 -translate-x-1/2 top-0"
                          style={{
                            width: 1,
                            height: spineHeight,
                            background:
                              'linear-gradient(to bottom, color-mix(in srgb, var(--info) 65%, transparent), color-mix(in srgb, var(--info) 20%, transparent) 55%, color-mix(in srgb, var(--info) 5%, transparent))',
                          }}
                        />
                      )}

                      {/* Week Monday label */}
                      <div
                        className={`grid place-items-center text-[10px] leading-none font-medium tabular-nums whitespace-nowrap ${
                          isTodayWeek ? 'text-[color-mix(in_srgb,var(--info)_60%,var(--text-strong))]' : 'text-[var(--muted)]'
                        }`}
                        style={{ height: WEEK_LABEL_HEIGHT }}
                      >
                        {column.weekStart.toLocaleDateString(undefined, SHORT_MONTH_DAY)}
                      </div>

                      {column.days.map((day) => {
                        const dayMs = day.getTime()
                        const hits = entitiesForDay(day, cellSpans)
                        const colors = hits.map((h) => h.color)
                        const bg = stripeBackground(colors)
                        const isFramed =
                          frameSpan !== null && dayMs >= frameSpan.startMs && dayMs <= frameSpan.endMs
                        const isToday = dayMs === todayMs
                        const dayKey = dateKey(day)
                        const label = day.toLocaleDateString(undefined, {
                          weekday: 'short',
                          month: 'short',
                          day: 'numeric',
                          year: 'numeric',
                        })
                        const hitTitlesShown = hits.slice(0, 4).map((h) => h.entity.title)
                        const overflow = hits.length > hitTitlesShown.length
                          ? ` (+${hits.length - hitTitlesShown.length} more)`
                          : ''
                        const titleText = hits.length === 0
                          ? (isFramed ? `${label} · in scope, no items` : label)
                          : `${label} · ${hitTitlesShown.join(' · ')}${overflow}`
                        const hoverText = hits.length === 0
                          ? (isFramed ? `${label}\nIn scope · no items` : label)
                          : `${label}\n${hitTitlesShown.join('\n')}${overflow}`
                        const isInteractive = hits.length > 0 && currentLevel !== 'subtask'

                        const doDrill = (stripeIndex: number) => {
                          if (!isInteractive || hits.length === 0) return
                          const clamped = Math.min(hits.length - 1, Math.max(0, stripeIndex))
                          const target = hits[clamped].entity
                          if (currentLevel === 'dashboard' && target.type === 'GOAL') {
                            goToGoal(target.id)
                          } else if (currentLevel === 'goal' && target.type === 'TASK' && activeGoalId) {
                            goToTask(activeGoalId, target.id)
                          } else if (
                            currentLevel === 'task' &&
                            target.type === 'SUBTASK' &&
                            activeGoalId &&
                            activeTaskId
                          ) {
                            goToSubTask(activeGoalId, activeTaskId, target.id)
                          }
                        }
                        const handleCellClick = (event: React.MouseEvent<HTMLDivElement>) => {
                          if (!isInteractive || hits.length === 0) return
                          const rect = event.currentTarget.getBoundingClientRect()
                          const relativeY = event.clientY - rect.top
                          doDrill(Math.floor((relativeY / rect.height) * hits.length))
                        }
                        const handleKey = (event: React.KeyboardEvent<HTMLDivElement>) => {
                          if (event.key !== 'Enter' && event.key !== ' ') return
                          event.preventDefault()
                          doDrill(Math.floor(hits.length / 2))
                        }
                        const cellBg = bg ?? (isFramed ? IN_SCOPE_EMPTY_GRAY : DEFAULT_GRAY)
                        return (
                          <div
                            key={dayKey}
                            title={titleText}
                            data-hover-info={hoverText}
                            role={isInteractive ? 'button' : undefined}
                            tabIndex={isInteractive ? 0 : undefined}
                            aria-label={isInteractive ? titleText : undefined}
                            onClick={handleCellClick}
                            onKeyDown={handleKey}
                            className={`relative w-full aspect-square will-change-transform ${
                              isInteractive
                                ? 'cursor-pointer hover:brightness-110 transition-[filter] focus-visible:outline focus-visible:outline-2 focus-visible:outline-[var(--accent)]'
                                : ''
                            }`}
                            style={{
                              borderRadius: '42%',
                              background: cellBg,
                              boxShadow: isFramed && frameSpan
                                ? `0 0 0 1.5px ${frameSpan.color}, var(--shadow-card)`
                                : undefined,
                            }}
                          >
                            {/* Stripe dividers between overlapping entities */}
                            {hits.length > 1 && Array.from({ length: hits.length - 1 }).map((_, i) => (
                              <span
                                key={`div-${i}`}
                                aria-hidden
                                className="pointer-events-none absolute left-0 right-0 h-px bg-[var(--border-subtle)]"
                                style={{
                                  top: `${((i + 1) / hits.length) * 100}%`,
                                }}
                              />
                            ))}
                            {/* Stronger today ring (inset + soft outer glow) */}
                            {isToday && (
                              <span
                                aria-hidden
                                className="pointer-events-none absolute inset-0"
                                style={{
                                  borderRadius: '42%',
                                  boxShadow:
                                    'inset 0 0 0 2px color-mix(in srgb, var(--info) 90%, transparent), var(--shadow-card)',
                                }}
                              />
                            )}
                          </div>
                        )
                      })}
                    </div>
                  )
                })}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

const buildLastThirtyDays = () => {
  const end = startOfDay(new Date())
  return Array.from({ length: 30 }, (_, index) => addDays(end, index - 29))
}

const activityTone = (count: number) => {
  if (count >= 12) return 'bg-[var(--chart-2)]'
  if (count >= 7) return 'bg-[var(--chart-1)]'
  if (count >= 3) return 'bg-[var(--chart-3)]'
  if (count >= 1) return 'bg-[var(--chart-4)]'
  return 'bg-[var(--control)]'
}

const activityCellStyle = (count: number, maxCount: number): CSSProperties => {
  if (count <= 0) {
    return {
      background:
        'linear-gradient(135deg, color-mix(in srgb, var(--control) 82%, transparent), color-mix(in srgb, var(--surface) 92%, transparent))',
    }
  }

  const intensity = Math.max(0.22, Math.min(1, count / Math.max(1, maxCount)))
  const token = count >= 12 ? 'var(--chart-2)' : count >= 7 ? 'var(--chart-1)' : count >= 3 ? 'var(--chart-3)' : 'var(--chart-4)'
  const edgeToken = count >= 12 ? 'var(--chart-6)' : count >= 7 ? 'var(--chart-5)' : count >= 3 ? 'var(--chart-7)' : 'var(--chart-8)'

  return {
    background: [
      `linear-gradient(135deg, color-mix(in srgb, ${token} ${(58 + intensity * 34).toFixed(0)}%, transparent), color-mix(in srgb, ${edgeToken} ${(34 + intensity * 32).toFixed(0)}%, transparent))`,
      `color-mix(in srgb, var(--control) 40%, transparent)`,
    ].join(', '),
    boxShadow: `0 0 0 1px color-mix(in srgb, ${token} 30%, transparent), var(--shadow-card)`,
  }
}

/** Deterministic accent for an actor id — uses the chart token ramp. */
const ACTOR_PALETTE = [
  'var(--chart-1)',
  'var(--chart-2)',
  'var(--chart-3)',
  'var(--chart-4)',
  'var(--chart-5)',
  'var(--chart-6)',
  'var(--chart-7)',
  'var(--chart-8)',
]
const actorAccent = (actorId: string): string => {
  if (!actorId) return ACTOR_PALETTE[0]
  let hash = 0
  for (let i = 0; i < actorId.length; i += 1) {
    hash = (hash * 31 + actorId.charCodeAt(i)) | 0
  }
  return ACTOR_PALETTE[Math.abs(hash) % ACTOR_PALETTE.length]
}

const initialsOf = (name: string): string => {
  const trimmed = (name || '').trim()
  if (!trimmed) return '?'
  const parts = trimmed.split(/\s+/).slice(0, 2)
  return parts.map((p) => p.charAt(0).toUpperCase()).join('') || trimmed.charAt(0).toUpperCase()
}

/** Compact per-day summary rendered inside the hover popover. */
type DaySummary = {
  total: number
  byType: Array<{ type: string; count: number }>
  topActor: { name: string; count: number } | null
  topTarget: { title: string; count: number } | null
}

const summarizeDay = (records: readonly WorkspaceActivityRecord[]): DaySummary => {
  const byType = new Map<string, number>()
  const byActor = new Map<string, { name: string; count: number }>()
  const byTarget = new Map<string, { title: string; count: number }>()
  for (const r of records) {
    byType.set(r.activityType, (byType.get(r.activityType) ?? 0) + 1)
    if (r.actorId) {
      const cur = byActor.get(r.actorId) ?? { name: r.actorName || 'Someone', count: 0 }
      cur.count += 1
      cur.name = r.actorName || cur.name
      byActor.set(r.actorId, cur)
    }
    if (r.targetId) {
      const cur = byTarget.get(r.targetId) ?? { title: r.targetTitle || 'Untitled', count: 0 }
      cur.count += 1
      cur.title = r.targetTitle || cur.title
      byTarget.set(r.targetId, cur)
    }
  }
  const typeList = Array.from(byType.entries())
    .map(([type, count]) => ({ type, count }))
    .sort((a, b) => b.count - a.count)
    .slice(0, 4)
  const actorList = Array.from(byActor.values()).sort((a, b) => b.count - a.count)
  const targetList = Array.from(byTarget.values()).sort((a, b) => b.count - a.count)
  return {
    total: records.length,
    byType: typeList,
    topActor: actorList[0] ?? null,
    topTarget: targetList[0] ?? null,
  }
}

const formatDayLabel = (key: string): string => {
  const parsed = parseDate(key)
  if (!parsed) return key
  return parsed.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' })
}

export const HeatmapView = ({
  activityByDate,
  activityRecords,
  isActivityLoading,
  elements,
  onElementClick,
}: DashboardViewProps) => {
  const days = useMemo(buildLastThirtyDays, [])
  const totalActions = activityRecords.length
  const activeMembers = useMemo(
    () => new Set(activityRecords.map((activity) => activity.actorId).filter(Boolean)).size,
    [activityRecords],
  )
  const busiestDay = useMemo(() => {
    let best: { key: string; count: number } | null = null
    for (const day of days) {
      const key = dateKey(day)
      const count = activityByDate.get(key)?.length ?? 0
      if (!best || count > best.count) best = { key, count }
    }
    return best
  }, [activityByDate, days])
  const activityCounts = useMemo(
    () => days.map((day) => activityByDate.get(dateKey(day))?.length ?? 0),
    [activityByDate, days],
  )
  const maxCount = Math.max(1, ...activityCounts)
  const activeDays = activityCounts.filter((count) => count > 0).length
  const averageDailyActivity = totalActions > 0 ? (totalActions / days.length).toFixed(1) : '0.0'
  const todayKey = dateKey(new Date())

  const elementById = useMemo(() => {
    const map = new Map<string, TimelineElement>()
    for (const el of elements) map.set(el.id, el)
    return map
  }, [elements])

  // Selection: click a cell (or a stat tile) to filter the recent-activity feed
  // to just that day. Clicking the same cell again clears the selection.
  const [selectedDateKey, setSelectedDateKey] = useState<string | null>(null)

  const toggleSelectedDay = (key: string, hasRecords: boolean) => {
    if (!hasRecords) {
      setSelectedDateKey(null)
      return
    }
    setSelectedDateKey((prev) => (prev === key ? null : key))
  }

  // Hover popover — fixed positioning based on viewport coords so it never
  // gets clipped by the scrolling parent.
  const [hover, setHover] = useState<
    | { dateKey: string; x: number; y: number; summary: DaySummary; label: string }
    | null
  >(null)

  const handleCellEnter = (event: React.MouseEvent<HTMLDivElement>, key: string, records: WorkspaceActivityRecord[], label: string) => {
    if (records.length === 0) {
      setHover(null)
      return
    }
    const rect = event.currentTarget.getBoundingClientRect()
    setHover({
      dateKey: key,
      x: rect.left + rect.width / 2,
      y: rect.top,
      summary: summarizeDay(records),
      label,
    })
  }
  const handleCellLeave = () => setHover(null)

  // Per-member roll-up for the "who's active" strip.
  const memberBreakdown = useMemo(() => {
    const map = new Map<string, { id: string; name: string; count: number }>()
    for (const r of activityRecords) {
      if (!r.actorId) continue
      const cur = map.get(r.actorId) ?? { id: r.actorId, name: r.actorName || 'Someone', count: 0 }
      cur.count += 1
      cur.name = r.actorName || cur.name
      map.set(r.actorId, cur)
    }
    return Array.from(map.values()).sort((a, b) => b.count - a.count)
  }, [activityRecords])

  // Recent activity — either the full feed or filtered to the selected day.
  const feedRecords = useMemo(() => {
    if (!selectedDateKey) return activityRecords.slice(0, 12)
    const forDay = activityByDate.get(selectedDateKey) ?? []
    return forDay.slice(0, 40)
  }, [activityRecords, activityByDate, selectedDateKey])

  const selectedLabel = selectedDateKey ? formatDayLabel(selectedDateKey) : null
  const canClickPeak = busiestDay !== null && busiestDay.count > 0

  return (
    <section className="grid h-full min-h-0 grid-rows-[auto_minmax(0,1fr)] gap-3">
      <header className="relative isolate overflow-hidden rounded-lg border border-[var(--border)] bg-[var(--surface-card)] p-3 shadow-[var(--shadow-card)] sm:p-4">
        <div
          className="pointer-events-none absolute inset-0 -z-10 opacity-[0.18]"
          style={{
            backgroundImage:
              'linear-gradient(color-mix(in srgb, var(--border-strong) 54%, transparent) 1px, transparent 1px), linear-gradient(90deg, color-mix(in srgb, var(--border-strong) 54%, transparent) 1px, transparent 1px)',
            backgroundSize: '36px 36px',
          }}
        />
        <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <p className="text-[10px] font-black uppercase tracking-[0.22em] text-[color-mix(in_srgb,var(--muted)_76%,var(--info))]">Activity Heatmap</p>
            <h3 className="m-0 text-base font-black text-[var(--text-strong)] sm:text-lg">Team productivity · 30 days</h3>
            <p className="m-0 mt-1 text-xs font-bold text-[var(--muted)]">
              {isActivityLoading ? 'Loading activity...' : `${totalActions} events · ${activeMembers} active member${activeMembers === 1 ? '' : 's'}`}
            </p>
          </div>
          <div className="grid grid-cols-3 gap-2 sm:min-w-[360px]">
            {([
              { label: 'Active days', value: String(activeDays), key: null as string | null, interactive: false },
              { label: 'Avg/day', value: averageDailyActivity, key: null, interactive: false },
              {
                label: 'Peak',
                value: busiestDay && busiestDay.count > 0 ? String(busiestDay.count) : '0',
                key: canClickPeak ? busiestDay!.key : null,
                interactive: canClickPeak,
              },
            ]).map((tile) => {
              const isActive = tile.interactive && tile.key !== null && tile.key === selectedDateKey
              const isInteractive = tile.interactive
              return (
                <Pressable
                  key={tile.label}
                  disabled={!isInteractive}
                  onClick={() => {
                    if (isInteractive && tile.key) toggleSelectedDay(tile.key, true)
                  }}
                  title={
                    isInteractive && tile.key
                      ? `Show the ${tile.value} events on ${formatDayLabel(tile.key)}`
                      : tile.label
                  }
                  className={`text-left rounded-md border px-3 py-2 shadow-[var(--shadow-card)] transition ${
                    isActive
                      ? 'border-[var(--info)] bg-[color-mix(in_srgb,var(--info)_18%,var(--control))] ring-1 ring-[var(--info)]'
                      : 'border-[color-mix(in_srgb,var(--border)_78%,transparent)] bg-[color-mix(in_srgb,var(--control)_58%,transparent)]'
                  } ${isInteractive ? 'cursor-pointer hover:border-[var(--info)] hover:bg-[color-mix(in_srgb,var(--info)_10%,var(--control))]' : 'cursor-default opacity-90'}`}
                >
                  <p className="m-0 text-[9px] font-black uppercase tracking-[0.16em] text-[var(--muted)]">{tile.label}</p>
                  <p className="m-0 mt-1 text-sm font-black tabular-nums text-[var(--text-strong)]">{tile.value}</p>
                </Pressable>
              )
            })}
          </div>
        </div>
        {busiestDay && busiestDay.count > 0 && (
          <div className="mt-3 h-1 overflow-hidden rounded-full bg-[color-mix(in_srgb,var(--control)_80%,transparent)]">
            <div
              className="h-full rounded-full bg-[linear-gradient(90deg,var(--chart-1),var(--chart-5),var(--chart-2))]"
              style={{ width: `${Math.min(100, Math.max(8, (activeDays / days.length) * 100))}%` }}
              title={`Peak ${busiestDay.count} activity events on ${busiestDay.key}`}
            />
          </div>
        )}

        {memberBreakdown.length > 0 && (
          <div className="mt-3 flex flex-wrap items-center gap-2">
            <span className="text-[9px] font-black uppercase tracking-[0.16em] text-[var(--muted)]">Who’s active</span>
            {memberBreakdown.slice(0, 8).map((member) => (
              <span
                key={member.id}
                title={`${member.name} · ${member.count} event${member.count === 1 ? '' : 's'}`}
                className="inline-flex items-center gap-1.5 rounded-full border border-[color-mix(in_srgb,var(--border)_78%,transparent)] bg-[color-mix(in_srgb,var(--control)_58%,transparent)] px-2 py-0.5"
              >
                <span
                  className="grid h-4 w-4 place-items-center rounded-full text-[8px] font-black text-[var(--surface)]"
                  style={{ background: actorAccent(member.id) }}
                >
                  {initialsOf(member.name)}
                </span>
                <span className="text-[10px] font-bold tabular-nums text-[var(--text-strong)]">{member.count}</span>
              </span>
            ))}
            {memberBreakdown.length > 8 && (
              <span className="text-[10px] font-bold text-[var(--muted)]">+{memberBreakdown.length - 8} more</span>
            )}
          </div>
        )}
      </header>

      <div data-dashboard-horizontal-scroll="true" className="relative isolate min-h-0 overflow-auto rounded-lg border border-[var(--border)] bg-[var(--surface)] p-4 shadow-[var(--shadow-card)]">
        <div className="grid min-w-[720px] grid-cols-[repeat(30,minmax(20px,1fr))] gap-2">
          {days.map((day, index) => {
            const key = dateKey(day)
            const records = activityByDate.get(key) ?? []
            const label = day.toLocaleDateString(undefined, SHORT_MONTH_DAY)
            const isToday = key === todayKey
            const hasActivity = records.length > 0
            const isSelected = selectedDateKey === key
            const isInteractive = hasActivity
            return (
              <div key={key} className="grid gap-2">
                <div
                  role={isInteractive ? 'button' : undefined}
                  tabIndex={isInteractive ? 0 : -1}
                  onClick={() => toggleSelectedDay(key, hasActivity)}
                  onKeyDown={(event) => {
                    if (!isInteractive) return
                    if (event.key === 'Enter' || event.key === ' ') {
                      event.preventDefault()
                      toggleSelectedDay(key, true)
                    }
                  }}
                  onMouseEnter={(event) => handleCellEnter(event, key, records, label)}
                  onMouseLeave={handleCellLeave}
                  className={`group relative aspect-square overflow-hidden rounded-md border transition duration-200 outline-none ${
                    hasActivity
                      ? 'border-[var(--border-strong)] hover:-translate-y-0.5 hover:scale-[1.04]'
                      : 'border-[color-mix(in_srgb,var(--border)_74%,transparent)] hover:border-[color-mix(in_srgb,var(--border-strong)_76%,transparent)]'
                  } ${isToday ? 'ring-1 ring-[var(--info)]' : ''} ${
                    isSelected ? 'ring-2 ring-[var(--info)] shadow-[var(--shadow-card)] scale-[1.06]' : ''
                  } ${isInteractive ? 'cursor-pointer focus:ring-2 focus:ring-[var(--info)]' : 'cursor-default'} ${activityTone(records.length)}`}
                  style={activityCellStyle(records.length, maxCount)}
                  title={`${label}: ${records.length} activity event${records.length === 1 ? '' : 's'}`}
                >
                  {hasActivity && (
                    <span className="pointer-events-none absolute bottom-1 left-1/2 h-1 w-1 -translate-x-1/2 rounded-full bg-[var(--text-strong)]/80" />
                  )}
                </div>
                <span className={`text-center text-[9px] font-bold tabular-nums ${isToday ? 'text-[color-mix(in_srgb,var(--info)_60%,var(--text-strong))]' : index % 5 === 0 ? 'text-[var(--text-strong)]' : 'text-[var(--muted)]'}`}>
                  {day.getDate()}
                </span>
              </div>
            )
          })}
        </div>

        <div className="mt-5 grid gap-2">
          <div className="flex items-center justify-between gap-2">
            <p className="m-0 text-[10px] font-black uppercase tracking-[0.16em] text-[var(--muted)]">
              {selectedDateKey ? 'Activity on selected day' : 'Recent activity'}
            </p>
            {selectedDateKey && selectedLabel && (
              <Button
                variant="secondary"
                size="sm"
                onClick={() => setSelectedDateKey(null)}
                className="gap-1"
              >
                <span className="tabular-nums normal-case tracking-normal">{selectedLabel}</span>
                <span className="inline-flex items-center gap-1">· clear <CloseRoundedIcon fontSize="inherit" /></span>
              </Button>
            )}
          </div>
          <div className="grid max-h-48 gap-2 overflow-auto pr-1">
            {feedRecords.map((activity) => {
              const linkedElement = activity.targetId ? elementById.get(activity.targetId) ?? null : null
              const isRowInteractive = linkedElement !== null
              const rowClass = `rounded-md border border-[var(--border)] bg-[color-mix(in_srgb,var(--surface)_82%,transparent)] px-3 py-2 text-left transition ${
                isRowInteractive
                  ? 'cursor-pointer hover:border-[var(--info)] hover:bg-[color-mix(in_srgb,var(--info)_10%,var(--surface))]'
                  : 'cursor-default'
              }`
              const timeLabel = activity.createdAt
                ? activity.createdAt.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })
                : null
              const content = (
                <>
                  <div className="flex items-center justify-between gap-3">
                    <strong className="min-w-0 truncate text-xs text-[var(--text-strong)]">{activity.targetTitle || 'Untitled'}</strong>
                    <span className="shrink-0 text-[10px] font-black uppercase tracking-wider text-[var(--muted)]">{activity.activityType}</span>
                  </div>
                  <p className="m-0 mt-1 truncate text-[11px] font-bold text-[var(--muted)]">{activity.summary}</p>
                  {(activity.actorName || timeLabel) && (
                    <p className="m-0 mt-0.5 truncate text-[10px] font-bold text-[color-mix(in_srgb,var(--muted)_82%,transparent)]">
                      {activity.actorName || 'Someone'}
                      {timeLabel ? ` · ${timeLabel}` : ''}
                    </p>
                  )}
                </>
              )
              if (isRowInteractive && linkedElement && onElementClick) {
                return (
                  <Pressable
                    key={activity.id}
                    onClick={() => onElementClick(linkedElement)}
                    className={rowClass + ' block w-full'}
                    title={`Open ${linkedElement.title}`}
                  >
                    {content}
                  </Pressable>
                )
              }
              return (
                <article key={activity.id} className={rowClass}>
                  {content}
                </article>
              )
            })}
            {!isActivityLoading && feedRecords.length === 0 && (
              <div className="grid min-h-28 place-items-center rounded-md border border-dashed border-[var(--border)] text-sm font-bold text-[var(--muted)]">
                {selectedDateKey
                  ? 'No activity recorded on this day.'
                  : 'No workspace activity in the last 30 days.'}
              </div>
            )}
          </div>
        </div>
      </div>

      {hover && (
        <div
          className="pointer-events-none fixed z-50 -translate-x-1/2 -translate-y-full"
          style={{ left: hover.x, top: hover.y - 8 }}
        >
          <div className="min-w-[180px] max-w-[240px] rounded-lg border border-[var(--border)] bg-[var(--surface)] p-2.5 text-[11px] shadow-[var(--shadow-pop)]">
            <div className="flex items-center justify-between gap-2">
              <span className="text-[10px] font-black uppercase tracking-[0.14em] text-[var(--muted)]">{hover.label}</span>
              <span className="rounded-full bg-[color-mix(in_srgb,var(--info)_18%,transparent)] px-1.5 py-0.5 text-[9px] font-black text-[color-mix(in_srgb,var(--info)_60%,var(--text-strong))]">
                {hover.summary.total} evt{hover.summary.total === 1 ? '' : 's'}
              </span>
            </div>
            {hover.summary.byType.length > 0 && (
              <div className="mt-1.5 flex flex-wrap gap-1">
                {hover.summary.byType.map((t) => (
                  <span
                    key={t.type}
                    className="rounded-full border border-[color-mix(in_srgb,var(--border)_76%,transparent)] bg-[color-mix(in_srgb,var(--control)_58%,transparent)] px-1.5 py-0.5 text-[9px] font-bold tabular-nums text-[var(--text-strong)]"
                  >
                    {t.type} {t.count}
                  </span>
                ))}
              </div>
            )}
            {hover.summary.topActor && (
              <p className="m-0 mt-1.5 truncate text-[10px] font-bold text-[var(--muted)]">
                Top actor · <span className="text-[var(--text-strong)]">{hover.summary.topActor.name}</span> ({hover.summary.topActor.count})
              </p>
            )}
            {hover.summary.topTarget && (
              <p className="m-0 truncate text-[10px] font-bold text-[var(--muted)]">
                Top target · <span className="text-[var(--text-strong)]">{hover.summary.topTarget.title}</span> ({hover.summary.topTarget.count})
              </p>
            )}
            <p className="m-0 mt-1 text-[9px] italic text-[color-mix(in_srgb,var(--muted)_80%,transparent)]">Click to filter the feed</p>
          </div>
        </div>
      )}
    </section>
  )
}

export const ContributionHeatmapView = {
  Grid: GridView,
  Heatmap: HeatmapView,
}
